import { getParsedMessage } from '@/data/error-messages';
import { ToastMessageOptions } from 'primevue/toast';
import { getCurrentInstance } from 'vue';

const DEFAULT_LIFE_IN_MS = 5000;

export function useToast() {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('useToast must be used within a setup function.');
  }

  const getParams = (params?: ToastMessageOptions) => {
    const { summary, life = DEFAULT_LIFE_IN_MS } = params ?? {};
    return {
      summary,
      life,
    };
  };

  const addToast = (toastOptions: ToastMessageOptions) => {
    instance.proxy?.$toast.add(toastOptions);
  };

  const success = (detail: string, params?: ToastMessageOptions) => {
    addToast({
      severity: 'success',
      detail,
      ...getParams(params),
    });
  };

  const error = (detail: string | Error, params?: ToastMessageOptions) => {
    const errorMessage =
      typeof detail === 'string' ? detail : getParsedMessage(detail, true);
    const summary = params?.summary || 'Something went wrong';
    if (errorMessage) {
      addToast({
        severity: 'error',
        ...getParams(params),
        detail: errorMessage,
        summary,
      });
    }
  };

  const info = (detail: string, params?: ToastMessageOptions) => {
    addToast({
      severity: 'info',
      detail,
      ...getParams(params),
    });
  };

  const warn = (detail: string, params?: ToastMessageOptions) => {
    addToast({
      severity: 'warn',
      detail,
      ...getParams(params),
    });
  };

  return {
    addToast,
    success,
    error,
    info,
    warn,
  };
}
